import axios from 'axios'
import { PaginatedResult, ProgressClaim, ProgressClaimReportBreakdownMode, ProgressClaimSummary } from '../models'
import { encodeBase64HtmlPayloadField } from '../../../shared/utils/base64HtmlPayload'
import { mockProgressClaimSummaries } from './mock-data'

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/ProgressClaims` : undefined
export const progressClaimsApiBaseUrl = API_URL

const ROLE_API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/Role`
  : undefined

export type ProgressClaimUserLookupItem = {
  id: string
  displayName: string
  email: string
  roles: string[]
}

export type RoleLookupItem = {
  id: string
  name?: string
  displayId?: number
}

export type UserSignatureResponse = {
  userId: string
  fileName: string
  contentType?: string
  size?: number
}

export type ProgressClaimReviewAction = 'approved' | 'reject' | 'finalReview'
export type ProgressClaimReviewActions = {
    canAct: boolean
    isFinalReview: boolean
    projectDirectors: ProgressClaimUserLookupItem[]
}

const getApiUrl = () => {
  if (!API_URL) {
    throw new Error('REACT_APP_API_URL is not configured.')
  }
  return API_URL
}

const delay = (ms = 400) => new Promise((resolve) => setTimeout(resolve, ms))

const encodeProgressClaimHtmlPayload = (payload: ProgressClaim): ProgressClaim => ({
  ...payload,
  letterhead: {
    ...payload.letterhead,
    coverBodyOverride: encodeBase64HtmlPayloadField(payload.letterhead.coverBodyOverride),
    coverTemplates: payload.letterhead.coverTemplates.map((template) => ({
      ...template,
      body: encodeBase64HtmlPayloadField(template.body),
      headerHtml: encodeBase64HtmlPayloadField(template.headerHtml),
      footerHtml: encodeBase64HtmlPayloadField(template.footerHtml),
    })),
    letterheadTemplates: payload.letterhead.letterheadTemplates.map((template) => ({
      ...template,
      body: encodeBase64HtmlPayloadField(template.body),
      headerHtml: encodeBase64HtmlPayloadField(template.headerHtml),
      footerHtml: encodeBase64HtmlPayloadField(template.footerHtml),
    })),
    footerTemplates: payload.letterhead.footerTemplates.map((template) => ({
      ...template,
      body: encodeBase64HtmlPayloadField(template.body),
      headerHtml: encodeBase64HtmlPayloadField(template.headerHtml),
      footerHtml: encodeBase64HtmlPayloadField(template.footerHtml),
    })),
  },
})

type SortValue = string | number | null | undefined

const parseSort = (sort?: string) => {
  if (!sort) return { fieldKey: 'claimnumber', dir: 'desc' as const }
  const parts = sort.trim().split(/\s+/)
  if (!parts.length || !parts[0]) return { fieldKey: 'claimnumber', dir: 'desc' as const }
  return {
    fieldKey: parts[0].toLowerCase(),
    dir: parts[1]?.toLowerCase() === 'desc' ? ('desc' as const) : ('asc' as const),
  }
}

const parseDateValue = (value?: string | null) => {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

const normalizeText = (value?: string | number | null) => String(value ?? '').trim().toLowerCase()

const extractNumericSearchTerm = (term: string) => {
  const numeric = term.replace(/[^\d.]/g, '').trim()
  return numeric.length ? numeric : ''
}

const resolveMockSortValue = (item: ProgressClaimSummary, fieldKey: string): SortValue => {
  switch (fieldKey) {
    case 'claimnumber':
      return item.claimNumber
    case 'dateofissue':
      return parseDateValue(item.dateOfIssue)
    case 'cumulativecompletedamount':
      return item.cumulativeCompletedAmount ?? null
    case 'recommendedamount':
      return item.recommendedAmount ?? null
    case 'status':
      return item.status ?? ''
    case 'updatedat':
    case 'lastmodified':
      return parseDateValue(item.updatedAt ?? item.dateOfIssue)
    default:
      return parseDateValue(item.updatedAt ?? item.dateOfIssue)
  }
}

const compareSortValues = (left: SortValue, right: SortValue) => {
  if (left === right) return 0
  if (left === null || left === undefined) return 1
  if (right === null || right === undefined) return -1
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

const revisionSuffixRegex = /-rev-(\d+)\s*$/i
const numberRegex = /\d+/g

const stripRevisionSuffix = (value?: string | null) => {
  if (!value) return ''
  return value.trim().replace(revisionSuffixRegex, '').trim()
}

const extractRevisionNumber = (value?: string | null) => {
  if (!value) return 0
  const match = value.trim().match(revisionSuffixRegex)
  if (!match?.[1]) return 0
  const parsed = Number(match[1])
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0
}

const normalizeNumericToken = (value: string) => {
  const normalized = value.replace(/^0+/, '')
  return normalized.length > 0 ? normalized : '0'
}

const compareNumericTokens = (left: string, right: string) => {
  if (left.length !== right.length) {
    return left.length - right.length
  }
  return left.localeCompare(right)
}

const extractClaimNumberTokens = (value?: string | null): string[] => {
  const base = stripRevisionSuffix(value)
  const matches = base.match(numberRegex)
  if (!matches || matches.length === 0) {
    return []
  }
  return matches.map((token) => normalizeNumericToken(token))
}

const compareClaimNumbers = (left?: string | null, right?: string | null) => {
  const leftTokens = extractClaimNumberTokens(left)
  const rightTokens = extractClaimNumberTokens(right)

  if (leftTokens.length === 0) {
    if (rightTokens.length === 0) {
      return normalizeText(left).localeCompare(normalizeText(right), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    }
    return 1
  }

  if (rightTokens.length === 0) {
    return -1
  }

  const tokenCount = Math.min(leftTokens.length, rightTokens.length)
  for (let index = 0; index < tokenCount; index += 1) {
    const comparison = compareNumericTokens(leftTokens[index], rightTokens[index])
    if (comparison !== 0) {
      return comparison
    }
  }

  if (leftTokens.length !== rightTokens.length) {
    return leftTokens.length - rightTokens.length
  }

  const leftBase = stripRevisionSuffix(left)
  const rightBase = stripRevisionSuffix(right)
  const baseTextComparison = normalizeText(leftBase).localeCompare(normalizeText(rightBase), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
  if (baseTextComparison !== 0) {
    return baseTextComparison
  }

  const revisionComparison = extractRevisionNumber(left) - extractRevisionNumber(right)
  if (revisionComparison !== 0) {
    return revisionComparison
  }

  return normalizeText(left).localeCompare(normalizeText(right), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const sortMockProgressClaims = (items: ProgressClaimSummary[], sort?: string) => {
  const parsed = parseSort(sort)
  const sorted = [...items]
  sorted.sort((left, right) => {
    const comparison =
      parsed.fieldKey === 'claimnumber'
        ? compareClaimNumbers(left.claimNumber, right.claimNumber)
        : compareSortValues(
            resolveMockSortValue(left, parsed.fieldKey),
            resolveMockSortValue(right, parsed.fieldKey)
          )
    if (comparison !== 0) {
      return parsed.dir === 'desc' ? -comparison : comparison
    }

    const claimNumberComparison = compareClaimNumbers(left.claimNumber, right.claimNumber)
    if (claimNumberComparison !== 0) {
      return parsed.dir === 'desc' ? -claimNumberComparison : claimNumberComparison
    }

    return normalizeText(left.id).localeCompare(normalizeText(right.id), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  })
  return sorted
}

const matchesDateOnly = (source?: string | null, candidate?: string | null) => {
  if (!source || !candidate) return false
  const sourceDate = parseDateValue(source)
  const candidateDate = parseDateValue(candidate)
  if (sourceDate === null || candidateDate === null) return false
  const sourceIso = new Date(sourceDate).toISOString().slice(0, 10)
  const candidateIso = new Date(candidateDate).toISOString().slice(0, 10)
  return sourceIso === candidateIso
}

const applyMockStatus = (items: ProgressClaimSummary[], status?: string) => {
  const normalizedStatus = status?.trim().toLowerCase()
  if (!normalizedStatus || normalizedStatus === 'all') return items
  return items.filter((claim) => normalizeText(claim.status) === normalizedStatus)
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const matchesSearchText = (value: string | number | null | undefined, search: string) => {
  const source = String(value ?? '').trim()
  const term = search.trim()
  if (!source || !term) return false
  if (!term.includes('*')) return source.toLowerCase().includes(term.toLowerCase())

  return new RegExp(escapeRegExp(term).replace(/\\\*/g, '.*'), 'i').test(source)
}

const applyMockSearch = (items: ProgressClaimSummary[], search?: string) => {
  const term = search?.trim() ?? ''
  if (!term) return items

  return items.filter((claim) =>
    matchesSearchText(claim.claimNumber, term) ||
    matchesSearchText(claim.dateOfIssue, term) ||
    matchesSearchText(claim.statusLabel, term)
  )
}

const applyMockFilters = (items: ProgressClaimSummary[], filter?: string) => {
  if (!filter) return items

  let parsed: Record<string, unknown> | null = null
  try {
    parsed = JSON.parse(filter) as Record<string, unknown>
  } catch {
    return items
  }

  if (!parsed || Object.keys(parsed).length === 0) return items

  return items.filter((item) =>
    Object.entries(parsed as Record<string, unknown>).every(([key, rawValue]) => {
      if (rawValue === null || rawValue === undefined || rawValue === '') return true
      const value = String(rawValue).trim()
      const normalizedValue = value.toLowerCase()

      switch (key.toLowerCase()) {
        case 'claimnumber':
          return normalizeText(item.claimNumber).includes(normalizedValue)
        case 'dateofissue':
          return matchesDateOnly(item.dateOfIssue, value)
        case 'cumulativecompletedamount': {
          const amountSearch = extractNumericSearchTerm(value)
          if (!amountSearch) return false
          return String(item.cumulativeCompletedAmount ?? '').includes(amountSearch)
        }
        case 'recommendedamount': {
          const amountSearch = extractNumericSearchTerm(value)
          if (!amountSearch) return false
          return String(item.recommendedAmount ?? '').includes(amountSearch)
        }
        case 'status':
          return normalizeText(item.status) === normalizedValue
        default:
          return true
      }
    })
  )
}

const toPaginatedResult = (items: ProgressClaimSummary[], options?: { skip?: number; take?: number }) => {
  const start = options?.skip ?? 0
  const take = options?.take && options.take > 0 ? options.take : items.length || 1
  const paged = items.slice(start, start + take)
  const pageNumber = Math.floor(start / Math.max(take, 1)) + 1
  return {
    items: paged,
    totalCount: items.length,
    pageNumber,
    totalPages: Math.max(1, Math.ceil(items.length / Math.max(take, 1))),
  }
}

export const listProgressClaims = async (options?: {
  projectId?: string | number
  status?: string
  search?: string
  skip?: number
  take?: number
  sort?: string
  filter?: string
}): Promise<PaginatedResult<ProgressClaimSummary>> => {
  if (API_URL) {
    try {
      const params: Record<string, string> = {}
      if (options?.projectId) params.projectId = options.projectId.toString()
      if (options?.status) params.status = options.status
      if (options?.search) params.search = options.search
      if (options?.skip !== undefined) params.skip = options.skip.toString()
      if (options?.take !== undefined) params.take = options.take.toString()
      if (options?.sort) params.sort = options.sort
      if (options?.filter) params.filter = options.filter

      const { data } = await axios.get<PaginatedResult<ProgressClaimSummary> | ProgressClaimSummary[] | any>(API_URL, {
        params,
      })

      if (Array.isArray(data)) {
        const filtered = applyMockFilters(
          sortMockProgressClaims(
            applyMockSearch(applyMockStatus(data as ProgressClaimSummary[], options?.status), options?.search),
            options?.sort
          ),
          options?.filter
        )
        return toPaginatedResult(filtered, options)
      }

      return {
        items: data.items ?? data.Items ?? [],
        totalCount: data.totalCount ?? data.TotalCount ?? (data.items?.length ?? 0),
        pageNumber: data.pageNumber ?? data.PageNumber ?? 1,
        totalPages: data.totalPages ?? data.TotalPages ?? 1,
      }
    } catch (error) {
      console.warn('Falling back to mock progress claims.', error)
    }
  }

  const filtered = applyMockFilters(
    sortMockProgressClaims(
      applyMockSearch(applyMockStatus(mockProgressClaimSummaries, options?.status), options?.search),
      options?.sort
    ),
    options?.filter
  )

  await delay(200)
  return toPaginatedResult(filtered, options)
}

export const getProgressClaim = async (options?: { claimId?: string; projectId?: number | string | null }): Promise<ProgressClaim> => {
  const apiUrl = getApiUrl()
  const claimId = options?.claimId
  const url = claimId ? `${apiUrl}/${claimId}` : `${apiUrl}/template`
  const params: Record<string, string> = {}
  if (!claimId && options?.projectId) {
    params.projectId = options.projectId.toString()
  }

  const { data } = await axios.get<ProgressClaim>(url, { params })
  return data
}

export const saveProgressClaimDraft = async (payload: ProgressClaim): Promise<ProgressClaim> => {
  const apiUrl = getApiUrl()
  const { data } = await axios.post<ProgressClaim>(apiUrl, encodeProgressClaimHtmlPayload(payload))
  return data
}

export const createProgressClaimRevision = async (claimId: string): Promise<ProgressClaim> => {
  const apiUrl = getApiUrl()
  const { data } = await axios.post<ProgressClaim>(`${apiUrl}/${claimId}/revision`)
  return data
}

export const unlockProgressClaim = async (claimId: string): Promise<ProgressClaim> => {
  const apiUrl = getApiUrl()
  const { data } = await axios.post<ProgressClaim>(`${apiUrl}/${claimId}/unlock`)
  return data
}

export const deleteProgressClaim = async (claimId: string): Promise<void> => {
  const apiUrl = getApiUrl()
  await axios.delete(`${apiUrl}/${claimId}`)
}

export const submitProgressClaim = async (payload: ProgressClaim): Promise<ProgressClaim> => {
  const apiUrl = getApiUrl()
  const { data } = await axios.post<ProgressClaim>(`${apiUrl}/submit`, encodeProgressClaimHtmlPayload(payload))
  return data
}

export const generateProgressClaimReport = async (
  claimId: string,
  breakdownMode: ProgressClaimReportBreakdownMode = 'parent-only'
) => {
  const apiUrl = getApiUrl()
  const { data } = await axios.post(`${apiUrl}/${claimId}/report`, undefined, {
    params: { breakdownMode },
    responseType: 'blob',
  })
  return data as Blob
}

export const downloadProgressClaimCharloJson = async (claimId: string) => {
  const apiUrl = getApiUrl()
  const { data } = await axios.get(`${apiUrl}/${claimId}/charlo-json`, { responseType: 'blob' })
  return data as Blob
}

export const searchProgressClaimUsers = async (searchTerm: string, roles: string[]) => {
  if (!API_URL) {
    return [] as ProgressClaimUserLookupItem[]
  }

  const params = new URLSearchParams()
  if (searchTerm) {
    params.append('term', searchTerm)
  }
  roles.forEach((role) => params.append('roles', role))

  const { data } = await axios.get<ProgressClaimUserLookupItem[]>(`${API_URL}/users`, { params })
  return data
}

export const getProgressClaimRoles = async (keyword?: string) => {
  if (!ROLE_API_URL) {
    return [] as RoleLookupItem[]
  }

  const params = new URLSearchParams()
  if (keyword) {
    params.append('keyword', keyword)
  }

  const { data } = await axios.get<RoleLookupItem[]>(`${ROLE_API_URL}/getallroles`, { params })
  return data
}

export const getProgressClaimUserSignature = async () => {
  if (!API_URL) {
    return undefined
  }

  try {
    const response = await axios.get<UserSignatureResponse>(`${API_URL}/user-signatures`)
    if (response.status === 204 || !response.data?.fileName) {
      return undefined
    }
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return undefined
    }
    throw error
  }
}

export const buildProgressClaimUserSignatureUrl = (fileName?: string) => {
  if (!API_URL || !fileName) {
    return undefined
  }
  return `${API_URL}/user-signatures/current/${encodeURIComponent(fileName)}`
}

export type SignatureSlot = 'preparedBy' | 'approvedBy'

export type SignatureUploadResponse = {
  fileName: string
  url?: string
  contentType?: string
  size?: number
}

export const uploadProgressClaimSignature = async (
  claimId: string,
  slot: SignatureSlot,
  file: File
): Promise<SignatureUploadResponse> => {
  const apiUrl = getApiUrl()

  const formData = new FormData()
  formData.append('file', file)

  const { data } = await axios.post<SignatureUploadResponse>(`${apiUrl}/${claimId}/signatures/${slot}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return {
    fileName: data.fileName,
    url: `${apiUrl}/${claimId}/signatures/${encodeURIComponent(data.fileName)}`,
    contentType: data.contentType,
    size: data.size,
  }
}

export const buildProgressClaimSignatureUrl = (claimId?: string, fileName?: string) => {
  if (!API_URL || !claimId || !fileName) {
    return undefined
  }
  return `${API_URL}/${claimId}/signatures/${encodeURIComponent(fileName)}`
}